import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import type {
  Notification,
  NotificationCursor
} from "../types/notification.types.ts"
import { countsTowardsBell } from "../helpers/countsTowardsBell.ts"
import { keepUnreadMembers } from "../helpers/keepUnreadMembers.ts"
import { markNotificationRead } from "../helpers/markNotificationRead.ts"
import { notificationsSliceName } from "./notification.constants.ts"
import type { NotificationState } from "./notification.types.ts"
import {
  fetchNotificationSettings,
  updateNotificationSettings
} from "./notifications.thunks.ts"

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  hasNewNotification: false,
  hasMore: false,
  next: null,
  notificationSettings: null,
  fetchingSettings: false,
  settingsError: false,
  isUpdatingSettings: false
}

const notificationSlice = createSlice({
  name: notificationsSliceName,
  initialState,
  reducers: {
    setInitialNotifications: (
      state,
      action: PayloadAction<{
        notifications: Notification[]
        unreadCount: number
        hasMore?: boolean
        next?: NotificationCursor
      }>
    ) => {
      state.notifications = action.payload.notifications
      state.unreadCount = action.payload.unreadCount
      state.hasMore = action.payload.hasMore || false
      state.next = action.payload.next || null
      state.hasNewNotification = false
    },

    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload)
      state.unreadCount += 1
    },

    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload
    },

    // No count arithmetic: the server answers every read with the
    // authoritative `unread_count`, and an estimate that has to walk a
    // collapsed row's members to stay right is the bug, not the fix.
    markAsRead: (state, action: PayloadAction<number | number[] | null>) => {
      if (action.payload === null) {
        state.notifications = state.notifications.map(markNotificationRead)
        return
      }

      const read = new Set(
        Array.isArray(action.payload) ? action.payload : [action.payload]
      )
      state.notifications = state.notifications.map((n) =>
        read.has(n.id) ? markNotificationRead(n) : n
      )
    },

    archiveNotifications: (state, action: PayloadAction<number[]>) => {
      const archived = new Set(action.payload)
      state.notifications = state.notifications.filter(
        (n) => !archived.has(n.id)
      )
    },

    archiveReadNotifications: (state) => {
      state.notifications = state.notifications
        .map(keepUnreadMembers)
        .filter((n): n is Notification => n !== null)
    },

    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
    },

    setHasNewNotification: (state, action: PayloadAction<boolean>) => {
      state.hasNewNotification = action.payload
    },

    notificationArrived: (state, action: PayloadAction<Notification>) => {
      state.hasNewNotification = true
      if (countsTowardsBell(action.payload)) {
        state.unreadCount += 1
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, () => initialState)
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.fetchingSettings = true
        state.settingsError = false
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.fetchingSettings = false
        state.settingsError = false
        state.notificationSettings = action.payload
      })
      .addCase(fetchNotificationSettings.rejected, (state) => {
        state.fetchingSettings = false
        state.settingsError = true
      })
      .addCase(updateNotificationSettings.pending, (state) => {
        state.isUpdatingSettings = true
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.isUpdatingSettings = false
        if (!state.notificationSettings) return

        const updatesMap = new Map(
          action.payload.map((p) => [p.notification_type, p])
        )

        state.notificationSettings = Object.fromEntries(
          Object.entries(state.notificationSettings).map(([key, items]) => [
            key,
            items.map((item) => ({
              ...item,
              ...updatesMap.get(item.notification_type)
            }))
          ])
        )
      })
      .addCase(updateNotificationSettings.rejected, (state) => {
        state.isUpdatingSettings = false
      })
  }
})

export const {
  setInitialNotifications,
  addNotification,
  markAsRead,
  setConnected,
  setHasNewNotification,
  notificationArrived,
  archiveNotifications,
  archiveReadNotifications,
  setUnreadCount
} = notificationSlice.actions

export default notificationSlice
